<script lang="ts" setup>
import { ref, computed, watch, h } from 'vue';
import { storeToRefs } from 'pinia';
import { useOrderCalendarStore } from '../pages/orderCalendarStore';
import { CalendarOrderService } from '../domains/services/CalendarOrderService';
import Modal from '@/components/modal/Modal.vue';
import { i18n } from '@/i18n';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';
import OrderAlert from '@/corePagesModule/BaseTableContent/components/order/OrderAlert.vue';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import EditableTableCell from '@/corePagesModule/BaseTableContent/components/EditableTableCell.vue';
import Btn from '@/components/input/Btn/Btn.vue';
import { CalendarOrder } from '../domains/entities/CalendarOrder';
import { simpleCopy } from '@/utils/deepCopy';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { useRouter } from 'vue-router';
import { DateService } from '@/coreDomainsModule/services/dateService';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import { IOrderGroup } from '@/coreDomainsModule/entities/IOrderGroup';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import OrderGroupInfoSection from '@/corePagesModule/components/OrderGroup/OrderGroupInfoSection.vue';

const t = i18n.t;

const router = useRouter();

const orderCalendarStore = useOrderCalendarStore();
const checkIds = ref<string[]>([]);

const { state } = storeToRefs(orderCalendarStore);
const { getOrdersByDate, setModalInfo, saveOrders, updateStateOrders } = orderCalendarStore;

const emits = defineEmits<{
  (e: 'onSaved', isSuccess: boolean): void;
}>();

const orders = computed(() =>
  state.value.modalInfo.day !== undefined ? simpleCopy(getOrdersByDate(state.value.modalInfo.day)) : undefined
);
const changeableOrders = computed(() => orders.value?.filter((order) => CalendarOrderService.isChangeableCrossDate(order)) ?? []);
const fixedOrders = computed(() => orders.value?.filter((order) => !CalendarOrderService.isChangeableCrossDate(order)) ?? []);
// 変更された発注を取得する
const amountChangedOrders = computed(() => orders.value?.filter((order) => changedOrderIds.value.includes(order.arrivalId)) ?? []);
const sumAmountByFixed = computed(() => {
  return changeableOrders.value.reduce((acc, order) => acc + order.amount, 0);
});
const sumAmountByChangeable = computed(() => {
  return fixedOrders.value.reduce((acc, order) => acc + order.amount, 0);
});
// 単位を取得する
const unit = computed(() => {
  if (orders.value && orders.value.length > 0) {
    return orders.value[0].unit;
  }
  return '';
});

// ヘッダー要素の高さ
const fixedOrdersTableHead = ref<HTMLTableSectionElement | null>(null);
// テーブル全体の高さ
const maxTableHeight = ref(0);
// 数量編集モードかどうか
const isEditingAmount = ref(false);
// 変更された発注ID
const changedOrderIds = ref<string[]>([]);
// 発注ステータス編集モードかどうか
const isEditingStatus = ref(false);
// アラートモーダルを開いているindex
const alertModalIndex = ref<number | null>(null);

watch(
  () => alertModalIndex.value,
  (newVal, oldVal) => {
    console.log('alertModalIndex', oldVal, newVal);
  }
);

// テーブルのgrid-template-columns
const gridTemplateColumns: string[] = ['50px', '120px', '155px', '155px', '155px', '80px', '155px', '155px', '90px', '110px', '90px'];

/**
 * モーダルを閉じるボタンがクリックされたときに呼び出されるハンドラ
 * @param isResetDay - 日付をリセットするかどうか
 */
const closeModal = (isResetDay?: boolean) => {
  const day = isResetDay ? undefined : state.value.modalInfo.day;
  setModalInfo(false, day);
};

/**
 * モーダルの状態をリセットする
 */
const reset = () => {
  checkIds.value = [];
  isEditingAmount.value = false;
  isEditingStatus.value = false;
  changedOrderIds.value = [];
  resetEditingValue();
};

/**
 * 変更ボタンがクリックされたときに呼び出されるハンドラ
 */
const clickSubmitChange = async () => {
  // 上限数量チェック
  if (amountChangedOrders.value.some((order) => order.orderGroupId)) {
    const checkUpperLimitQuantityRes = await checkOrderGroupUpperLimitQuantity();
    if (!checkUpperLimitQuantityRes) return;
  }

  // checkIdsをリセット
  checkIds.value = [];

  // ステータス編集モードに移行
  isEditingAmount.value = false;
  isEditingStatus.value = true;
};

/**
 * 数量編集した発注が発注グループの上限に引っかかってないかを確認する
 */
const checkOrderGroupUpperLimitQuantity = async (): Promise<boolean> => {
  if (state.value.modalInfo.day === undefined) return false;
  const orderGroupsWithLinkedOrderList: Map<IOrderGroup, CalendarOrder[]> = new Map(
    state.value.orderGroupList.map((group) => [
      group,
      amountChangedOrders.value.filter((order) => order.orderGroupId === group.orderGroupId),
    ])
  );
  let invalid: { orderGroup: IOrderGroup; changedAmountDiff: number }[] = [];
  const originalOrderList = getOrdersByDate(state.value.modalInfo.day);
  orderGroupsWithLinkedOrderList.forEach((orderList, orderGroup) => {
    if (!orderGroup || orderList.length === 0) return;
    const changedAmountDiff = orderList.reduce((acc, order) => {
      acc += order.amount - (originalOrderList.find((originalOrder) => originalOrder.arrivalId === order.arrivalId)?.amount ?? 0);
      return acc;
    }, 0);
    if (orderGroup.totalLinkedOrderAmount + changedAmountDiff > orderGroup.upperLimitQuantity) {
      invalid.push({ orderGroup, changedAmountDiff });
    }
  });
  if (invalid.length > 0) {
    await showInfoModal({
      modalTitle: t('発注グループの上限数量オーバー'),
      modalDescription: t('発注グループの上限数量の引き上げ、または各発注の数量を上限数量に収まるようにしてください。'),
      modalContent: h(
        'div',
        {
          style: {
            padding: '16px',
            display: 'grid',
            gridTemplateRows: `repeat(${invalid.length}, auto)`,
            borderRadius: '8px',
            backgroundColor: '#f5f5f5',
          },
        },
        invalid.map((data) =>
          h(OrderGroupInfoSection, {
            orderGroupName: data.orderGroup.orderGroupName,
            numberOfOrder: data.orderGroup.linkedOrderCount,
            upperLimitQuantity: data.orderGroup.upperLimitQuantity,
            totalAmount: data.orderGroup.totalLinkedOrderAmount,
            afterTotalAmount: data.orderGroup.totalLinkedOrderAmount + data.changedAmountDiff,
            hasBackground: false,
          })
        )
      ),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
      modalWidth: 764,
    });
    return false;
  }
  return true;
};

/**
 * 承認依頼ボタンがクリックされたときに呼び出されるハンドラ
 * @param isChange - ステータスを変更するかどうか
 */
const clickSubmitStatusChange = async (isChange: boolean) => {
  const targetIds = changedOrderIds.value;
  if (isChange) {
    // ステータスを変更する場合
    if (targetIds.length > 0) {
      // 変更された発注のステータスを変更する
      const targetOrders = orders.value?.filter((order) => targetIds.includes(order.arrivalId));
      targetOrders?.forEach((order) => {
        order.status = OrderStatus.ApprovalRequested;
      });
    }
  }

  // 変更された発注がない場合は何もしない
  // NOTE: ここで発注が存在しないことは想定されていない
  if (targetIds.length === 0) {
    reset();
    closeModal(true);
    return;
  }

  // 変更した発注データを取得
  const changedOrders = orders.value?.filter((order) => changedOrderIds.value.includes(order.arrivalId));

  // モーダルを閉じる
  closeModal();

  // 変更された発注を保存する
  const repository = new OrderRepository();
  const result = await waitLoading(saveOrders(repository, changedOrders));
  reset();

  emits('onSaved', result?.status.type === 'success');
};

/**
 * 数量変更ボタン押下時のハンドラ
 */
const clickChangeAmount = () => {
  isEditingAmount.value = true;
};

/**
 * キャンセルボタン押下時のハンドラ
 */
const clickCancel = async () => {
  resetEditingValue();
  isEditingAmount.value = false;
};

/**
 * 編集した値をリセットする
 */
const resetEditingValue = () => {
  const day = state.value.modalInfo.day;
  state.value.modalInfo.day = undefined;
  state.value.modalInfo.day = day;
};

/**
 * 数量が編集されたときに呼び出されるハンドラ
 * @param order - 変更された発注
 * @param amountStr - 変更された数量
 */
const changeOrderAmount = (order: CalendarOrder, amountStr: string): void => {
  // number型に変換する
  const amount = parseInt(amountStr, 10);
  if (Number.isNaN(amount)) {
    console.error('数量が数値ではありません');
    return;
  }

  // 数量が変更されているかどうかを確認する
  if (order.amount === amount) {
    return;
  }

  // 変更された数量をセットする
  order.amount = amount;

  // 変更された発注をメモする
  if (!changedOrderIds.value.includes(order.arrivalId)) {
    changedOrderIds.value.push(order.arrivalId);
  }
};

/**
 * 発注が変更可能かどうかを判定する
 * @param order - 発注
 */
const isChangeable = (order: CalendarOrder): boolean => {
  return CalendarOrderService.isChangeableQuantity(order);
};

/**
 * アウトサイドクリック時のハンドラ
 */
const handleOutsideClick = () => {
  reset();
  closeModal();
};

/**
 * アラート内の確認ボタンをクリックした際の処理
 * @param alertKind - アラートの種類
 * @param order - 発注
 */
const onClickCheckButtonInAlert = (alertKind: string, order: CalendarOrder) => {
  if (alertKind === 'orderAmountWarning') {
    // 詳細ページに移動する
    window.open(`#/business/recommend-order/${order.parentBusinessId}`, '_blank');
  }
};

/**
 * deliveryDateの文字列を取得する
 * @param order - 発注
 */
const getDeliveryDate = (order: CalendarOrder): string => {
  if (order.crossDate) {
    return DateService.convertIYearMonthDayToYYYYMMDDWithi18n(order.crossDate);
  }
  return '';
};

/**
 * アラートモーダルの開閉を切り替える
 * @param index - 対象のインデックス
 * @param toOpenModal - 開こうとしているかどうか
 */
const toggleAlertModal = (index: number, toOpenModal: boolean) => {
  if (toOpenModal) {
    // 開こうとしている場合はindexをセット
    alertModalIndex.value = index;
    return;
  }

  // 閉じようとしている場合
  if (alertModalIndex.value !== index) {
    // 対象のindexと開かれているindexが異なる場合、既にindexが切り替わっているため何もしない
    return;
  }
  // 正常に閉じれる場合はnullにセット
  alertModalIndex.value = null;
};
</script>
<template>
  <Modal
    :modal-state="state.modalInfo.isOpen"
    :stretch-container="true"
    :scroll-off="true"
    padding="0"
    border-radius="16px"
    :no-padding-and-margin="false"
    modal-size="max-content"
    :is-force-no-style="true"
    @outside-click="handleOutsideClick"
  >
    <div class="day-modal">
      <div class="day-modal__title">
        {{
          t('発注カレンダー(モーダル)', {
            year: state.modalInfo.day?.year,
            month: state.modalInfo.day?.month,
            day: state.modalInfo.day?.day,
          })
        }}
      </div>
      <div v-if="isEditingStatus" class="day-modal__description">
        {{ t('承認依頼を行う発注を選択してください。') }}
      </div>
      <div class="day-modal__table-container">
        <div class="day-modal__table-container--content day-modal__content">
          <div v-if="!isEditingStatus" class="day-modal__content-title-and-sum">
            <div class="day-modal__content-title day-modal__content-title--confirm">{{ t('納期確定') }}</div>
            <div class="day-modal__content-sum">
              {{ t('合計：') }}<b class="day-modal__content--bold">{{ sumAmountByChangeable.addComma() }}{{ unit }}</b>
            </div>
            <div class="day-modal__content-title day-modal__content-title--pending">{{ t('納期未確定') }}</div>
            <div class="day-modal__content-sum">
              {{ t('合計：') }}<b class="day-modal__content-sum--bold">{{ sumAmountByFixed.addComma() }}{{ unit }}</b>
            </div>
            <Btn class="day-modal__change-amount-btn" button-style="btn-secondary" :disabled="isEditingAmount" @click="clickChangeAmount">{{
              t('数量を編集')
            }}</Btn>
          </div>
          <div class="day-modal__table-wrapper">
            <EzTable :grid-template-columns="gridTemplateColumns" sticky-header>
              <template #header>
                <EzHeader class="day-modal__table__header" />
                <EzHeader class="day-modal__table__header">
                  {{ t('集計ID') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('置場名') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('商品CD') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('商品名') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('容量') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('仕入先名') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('発注番号') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('発注数量') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('ステータス') }}
                </EzHeader>
                <EzHeader class="day-modal__table__header">
                  {{ t('アラート') }}
                </EzHeader>
              </template>
              <template #body>
                <EzTableRow
                  v-for="(order, index) in isEditingStatus ? amountChangedOrders : orders"
                  :id="`day-modal-table-row-${index}`"
                  :key="order.arrivalId"
                  :grid-template-columns="gridTemplateColumns"
                  class="day-modal__table__row"
                >
                  <EzCell class="day-modal__table__data" force-center>
                    <input v-model="checkIds" class="day-modal__table__checkbox" type="checkbox" :value="order.arrivalId" />
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    {{ order.parentBusinessId }}
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    {{ order.warehouse }}
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    {{ order.businessNumber }}
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    {{ order.businessName }}
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    {{ order.package }}
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    {{ order.supplierName }}
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    {{ order.number || '--' }}
                  </EzCell>
                  <EzCell class="day-modal__table__data" force-right>
                    <EditableTableCell
                      v-if="isEditingAmount && isChangeable(order)"
                      input-type="number"
                      :text-value="order.amount.toString()"
                      @change-text="changeOrderAmount(order, $event)"
                    />
                    <template v-else>{{ order.amount.addComma() }}</template>
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    <OrderStatusBadge :order-status-str="order.status" />
                  </EzCell>
                  <EzCell class="day-modal__table__data">
                    <OrderAlert
                      class="day-modal__table__alert"
                      :alert-types="state.orderAlertMap[order.arrivalId]"
                      :order-amount="order.amount.toString()"
                      :delivery-date="getDeliveryDate(order)"
                      :is-opening="alertModalIndex === index"
                      @click="toggleAlertModal(index, $event)"
                      @on-next="onClickCheckButtonInAlert($event, order)"
                    />
                  </EzCell>
                </EzTableRow>
              </template>
            </EzTable>
          </div>
        </div>
      </div>
      <div v-if="isEditingAmount" class="day-modal__button-container">
        <Btn class="day-modal__button day-modal__button--cancel" @click="clickCancel">{{ t('キャンセル') }}</Btn>
        <Btn class="day-modal__button day-modal__button--save" :disabled="changedOrderIds.length === 0" @click="clickSubmitChange">
          {{ t('変更') }}
        </Btn>
      </div>
      <div v-if="isEditingStatus" class="day-modal__button-container">
        <Btn class="day-modal__button day-modal__button--cancel" @click="clickSubmitStatusChange(false)">
          {{ t('承認依頼しない') }}
        </Btn>
        <Btn class="day-modal__button day-modal__button--save" :disabled="checkIds.length === 0" @click="clickSubmitStatusChange(true)">
          {{ t('承認依頼する') }}
        </Btn>
      </div>
    </div>
  </Modal>
</template>
<style scoped lang="scss">
.day-modal {
  padding: 24px 16px;
  display: grid;
  grid-template-rows: minmax(28px, 3.7%) 1fr;
  gap: 24px;
  background-color: $semanticWhite;
  width: max-content;

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
    text-align: center;
    color: $textDefault;
  }

  &__table-container {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));
  }

  &__content {
    background-color: $backgroundDefault;
    padding: 16px;
    grid-template-rows: fit-content(100%);
    display: grid;
    gap: 16px;

    border-radius: 8px;

    &-text {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 20.27px;
      text-align: left;
      color: $textDefault;
    }

    &-title-and-sum {
      display: grid;
      grid-template-columns: repeat(4, auto) 1fr;
      justify-content: start;
      align-items: center;
      gap: 24px;
    }

    &-title {
      font-family: Noto Sans JP;
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
      text-align: left;
      padding: 8px 12px;

      &--confirm {
        color: $semanticSuccess;
      }

      &--pending {
        color: $textSecondary;
        background-color: $semanticWhite;

        padding: 7px 11px;
        border: 1px solid $borderSecondary;
        border-radius: 4px;
      }
    }
    &-sum {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 14px;
      text-align: left;

      &--bold {
        font-family: Noto Sans JP;
        font-size: 14px;
        font-weight: 700;
        line-height: 14px;
        text-align: left;
      }
    }
  }

  &__table {
    // テーブルスタイル
    &__head {
      background-color: $surfaceSecondary;
      width: max-content;
      position: sticky;
      top: 0;
    }
    &__row {
      border-bottom: 1px solid $borderDefault;
    }
    &__header {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 700;
      line-height: 14px;
      color: $textDefault;
      padding: 11px 0;
      white-space: nowrap;
    }
    &__body {
      background-color: $semanticWhite;
      width: max-content;
    }
    &__data {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 14px;
      color: $textSecondary;
      padding: 8px 0;
    }
    &__alert {
      height: 24px;
    }
  }

  &__button {
    min-width: 160px;
    height: 40px;
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 40px;
    letter-spacing: 0em;
    text-align: center;
    border-radius: 4px;
    padding: 0 8px;
    box-shadow: none;

    &--cancel {
      color: $semanticPrimary;
      background-color: $semanticWhite;
      border: 1px solid $semanticPrimary;
    }
    &--save {
      color: $semanticWhite;
      background-color: $semanticPrimary;
      border: 1px solid $semanticPrimary;

      &:disabled {
        background-color: $surfaceSecondary;
        border: 1px solid $surfaceSecondary;
        color: $textDisable;
      }
    }

    &-container {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(2, auto);
      justify-content: center;
      padding-top: 16px;
    }
  }

  &__change-amount-btn {
    justify-self: end;

    width: 128px;
    height: 40px;
    padding: 10px 24px 10px 24px;
    gap: 4px;
    border-radius: 4px;

    box-shadow: none;

    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    white-space: nowrap;
  }
}

.day-modal__table-wrapper {
  height: 100%;
  overflow: auto;
  width: max-content;
  max-height: 50vh;
}

.day-modal__table__data--string {
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
}
.day-modal__table__data-number {
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
